export const REGISTRATION_FIELD_LABELS: Record<string, string> = {
  first_name: "First Name",
  last_name: "Last Name",
  email: "Email Address",
  phone: "Phone Number",
  date_of_birth: "Date of Birth",
  address: "Address",
  emergency_contact: "Emergency Contact",
  allergies: "Allergies/Medical Information",
  medical_info: "Medical Information",
  experience_level: "Martial Arts Experience",
  belt_rank: "Current Belt Rank",
  belt_size: "Belt Size",
  uniform_size: "Uniform Size",
  t_shirt_size: "T-Shirt Size",
  how_did_you_hear: "How Did You Hear About Us?",
  parent_name: "Parent/Guardian Name",
  parent_phone: "Parent/Guardian Phone",
  parent_email: "Parent/Guardian Email",
  terms_conditions: "Terms & Conditions",
};

const REGISTRATION_FIELD_DATA_KEYS: Record<string, string> = {
  first_name: "firstName",
  last_name: "lastName",
  email: "email",
  phone: "phone",
  date_of_birth: "dateOfBirth",
  address: "address",
  emergency_contact: "emergencyContact",
  allergies: "allergies",
  medical_info: "medicalInfo",
  experience_level: "experienceLevel",
  belt_rank: "beltRank",
  belt_size: "beltSize",
  uniform_size: "uniformSize",
  t_shirt_size: "tShirtSize",
  how_did_you_hear: "howDidYouHear",
  parent_name: "parentName",
  parent_phone: "parentPhone",
  parent_email: "parentEmail",
  terms_conditions: "termsConditions",
};

/** Registration fields rendered and validated as free-text inputs */
export const SHORT_TEXT_REGISTRATION_FIELD_IDS = [
  "experience_level",
  "belt_rank",
  "belt_size",
  "uniform_size",
  "t_shirt_size",
] as const;

const SUMMARY_TABLE_FIELDS = new Set([
  "first_name",
  "last_name",
  "email",
  "phone",
  "terms_conditions",
]);

export function getRegistrationFieldLabel(fieldId: string): string {
  return REGISTRATION_FIELD_LABELS[fieldId] || fieldId;
}

export function getRegistrationFormDataKey(fieldId: string): string | undefined {
  return REGISTRATION_FIELD_DATA_KEYS[fieldId];
}

export function getRegistrationFieldValue(
  data: Record<string, unknown> | null | undefined,
  fieldId: string
): string {
  if (!data) return "-";

  const key = REGISTRATION_FIELD_DATA_KEYS[fieldId];
  const value = key ? data[key] : data[fieldId];

  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

export function getExtraRegistrationColumns(registrationFields?: string[]): string[] {
  return (registrationFields || []).filter((fieldId) => !SUMMARY_TABLE_FIELDS.has(fieldId));
}

export function getRegistrationDetailFields(registrationFields?: string[]): string[] {
  if (registrationFields?.length) {
    return registrationFields;
  }

  return Object.keys(REGISTRATION_FIELD_LABELS);
}

export const HOW_DID_YOU_HEAR_OPTIONS = [
  "Referral",
  "Local Partnerships",
  "Community Events & Booths",
  "Walk-in",
  "Internet Search",
  "Internet Ads",
  "Website Lead Capture",
  "Cross-Promotions",
  "Email Marketing",
  "Social Media",
  "Social Media Advertisements",
  "Other",
] as const;

const TEXTAREA_FIELD_IDS = new Set([
  "address",
  "allergies",
  "medical_info",
  "emergency_contact",
]);

export function isRegistrationTextareaField(fieldId: string): boolean {
  return TEXTAREA_FIELD_IDS.has(fieldId);
}

export function buildRegistrationFormState(
  data: Record<string, unknown> | null | undefined,
  registrationFields?: string[]
): Record<string, string | boolean> {
  const formState: Record<string, string | boolean> = {};
  const fields = getRegistrationDetailFields(registrationFields);

  for (const fieldId of fields) {
    if (fieldId === "terms_conditions") {
      formState.termsConditions = Boolean(data?.termsConditions);
      continue;
    }

    const key = getRegistrationFormDataKey(fieldId);
    if (!key) continue;

    const value = data?.[key];
    formState[key] = value === null || value === undefined ? "" : String(value);
  }

  return formState;
}

export function validateRegistrationFormState(
  formState: Record<string, string | boolean>,
  registrationFields?: string[]
): Record<string, string> {
  const errors: Record<string, string> = {};
  const fields = registrationFields || [];

  const requireField = (fieldId: string, formKey: string, label: string) => {
    if (!fields.includes(fieldId)) return;
    const value = formState[formKey];
    if (typeof value !== "string" || !value.trim()) {
      errors[formKey] = `${label} is required`;
    }
  };

  if (!String(formState.firstName || "").trim()) {
    errors.firstName = "First name is required";
  }
  if (!String(formState.lastName || "").trim()) {
    errors.lastName = "Last name is required";
  }
  if (!String(formState.email || "").trim()) {
    errors.email = "Email is required";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(formState.email))) {
    errors.email = "Please enter a valid email address";
  }

  requireField("phone", "phone", "Phone number");
  requireField("date_of_birth", "dateOfBirth", "Date of birth");
  requireField("address", "address", "Address");
  requireField("emergency_contact", "emergencyContact", "Emergency contact");
  requireField("allergies", "allergies", "Allergies/medical information");
  requireField("medical_info", "medicalInfo", "Medical information");
  requireField("how_did_you_hear", "howDidYouHear", "How did you hear about us");
  requireField("parent_name", "parentName", "Parent/guardian name");
  requireField("parent_phone", "parentPhone", "Parent/guardian phone");
  requireField("parent_email", "parentEmail", "Parent/guardian email");

  SHORT_TEXT_REGISTRATION_FIELD_IDS.forEach((fieldId) => {
    const formKey = getRegistrationFormDataKey(fieldId);
    if (!formKey) return;
    requireField(fieldId, formKey, getRegistrationFieldLabel(fieldId));
  });

  if (fields.includes("parent_email")) {
    const parentEmail = String(formState.parentEmail || "").trim();
    if (parentEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(parentEmail)) {
      errors.parentEmail = "Please enter a valid email address";
    }
  }

  if (fields.includes("terms_conditions") && !formState.termsConditions) {
    errors.termsConditions = "Terms and conditions must be accepted";
  }

  return errors;
}

export function formStateToRegistrationData(
  formState: Record<string, string | boolean>
): Record<string, unknown> {
  const data: Record<string, unknown> = {};

  Object.entries(formState).forEach(([key, value]) => {
    if (typeof value === "boolean") {
      data[key] = value;
    } else {
      data[key] = value.trim();
    }
  });

  return data;
}
